import { Head, Link as InertiaLink, router } from "@inertiajs/react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  IconButton,
  ScrollArea,
  Section,
  Separator,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Calendar, MapPin, Minus, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { SeasonEventSummary } from "@/types/shared";

interface SeasonHeader {
  id: number;
  name: string;
  slug: string;
  track_name: string;
  track_slug: string;
}

interface Props {
  season: SeasonHeader;
  events_in_season: SeasonEventSummary[];
  available_events: SeasonEventSummary[];
}

function formatDate(iso: string | null) {
  if (!iso) return "TBD";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function filterByQuery(events: SeasonEventSummary[], query: string) {
  if (!query.trim()) return events;
  const q = query.trim().toLowerCase();
  return events.filter(
    (e) =>
      e.name.toLowerCase().includes(q) ||
      (e.track_name ?? "").toLowerCase().includes(q),
  );
}

interface EventRowProps {
  event: SeasonEventSummary;
  action: "add" | "remove";
  onClick: () => void;
}

function EventRow({ event, action, onClick }: EventRowProps) {
  return (
    <Card>
      <Flex align="center" justify="between" gap="3">
        <Flex direction="column" gap="1" className="min-w-0 grow">
          <Text size="3" weight="medium" truncate>
            {event.name}
          </Text>
          <Flex align="center" gap="3" wrap="wrap">
            <Flex align="center" gap="1">
              <MapPin size={12} />
              <Text size="1" color="gray">
                {event.track_name}
              </Text>
            </Flex>
            <Flex align="center" gap="1">
              <Calendar size={12} />
              <Text size="1" color="gray">
                {formatDate(event.date)}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <IconButton
          variant="soft"
          color={action === "add" ? "green" : "crimson"}
          size="2"
          onClick={onClick}
          aria-label={action === "add" ? "Add to season" : "Remove from season"}
        >
          {action === "add" ? <Plus size={16} /> : <Minus size={16} />}
        </IconButton>
      </Flex>
    </Card>
  );
}

export default function ManageSeasonEvents({
  season,
  events_in_season,
  available_events,
}: Props) {
  const [inSeasonQuery, setInSeasonQuery] = useState("");
  const [availableQuery, setAvailableQuery] = useState("");

  const filteredInSeason = useMemo(
    () => filterByQuery(events_in_season, inSeasonQuery),
    [events_in_season, inSeasonQuery],
  );
  const filteredAvailable = useMemo(
    () => filterByQuery(available_events, availableQuery),
    [available_events, availableQuery],
  );

  const addEvent = (eventId: number) => {
    router.post(
      `/track_manager/seasons/${season.slug}/events`,
      { event_id: eventId },
      { preserveScroll: true },
    );
  };

  const removeEvent = (eventId: number) => {
    router.delete(
      `/track_manager/seasons/${season.slug}/events/${eventId}`,
      { preserveScroll: true },
    );
  };

  return (
    <div className="flex">
      <Head title={`Manage Events – ${season.name}`} />
      <Box className="grow">
        <Section size="2">
          <Box className="max-w-6xl mx-auto px-4">
            <Flex direction="column" gap="2" mb="4">
              <Flex align="center" gap="2">
                <Button asChild variant="ghost" size="2" color="gray">
                  <InertiaLink href="/track_manager/seasons">
                    ← All Seasons
                  </InertiaLink>
                </Button>
              </Flex>
              <Heading size="7">{season.name}</Heading>
              <Flex align="center" gap="2">
                <MapPin size={14} />
                <Text size="2" color="gray">
                  {season.track_name}
                </Text>
                <Separator orientation="vertical" />
                <Text size="2" color="gray">
                  Manage Events
                </Text>
              </Flex>
            </Flex>

            <Flex
              direction={{ initial: "column", md: "row" }}
              gap="4"
              align="stretch"
            >
              <Card className="flex-1 min-w-0">
                <Flex direction="column" gap="3">
                  <Flex align="center" justify="between" gap="2">
                    <Heading size="4">In This Season</Heading>
                    <Badge variant="soft" size="2">
                      {events_in_season.length}
                    </Badge>
                  </Flex>
                  <TextField.Root
                    size="2"
                    placeholder="Search events…"
                    value={inSeasonQuery}
                    onChange={(e) => setInSeasonQuery(e.target.value)}
                  >
                    <TextField.Slot>
                      <Search size={14} />
                    </TextField.Slot>
                  </TextField.Root>
                  <ScrollArea
                    type="auto"
                    scrollbars="vertical"
                    className="h-[60vh]"
                  >
                    <Flex direction="column" gap="2" pr="2">
                      {filteredInSeason.length === 0 ? (
                        <Box py="6">
                          <Text as="p" align="center" color="gray" size="2">
                            {events_in_season.length === 0
                              ? "No events yet. Add some from the right →"
                              : "No events match your search."}
                          </Text>
                        </Box>
                      ) : (
                        filteredInSeason.map((event) => (
                          <EventRow
                            key={event.id}
                            event={event}
                            action="remove"
                            onClick={() => removeEvent(event.id)}
                          />
                        ))
                      )}
                    </Flex>
                  </ScrollArea>
                </Flex>
              </Card>

              <Card className="flex-1 min-w-0">
                <Flex direction="column" gap="3">
                  <Flex align="center" justify="between" gap="2">
                    <Heading size="4">Available Events</Heading>
                    <Badge variant="soft" size="2" color="gray">
                      {available_events.length}
                    </Badge>
                  </Flex>
                  <TextField.Root
                    size="2"
                    placeholder="Search events…"
                    value={availableQuery}
                    onChange={(e) => setAvailableQuery(e.target.value)}
                  >
                    <TextField.Slot>
                      <Search size={14} />
                    </TextField.Slot>
                  </TextField.Root>
                  <ScrollArea
                    type="auto"
                    scrollbars="vertical"
                    className="h-[60vh]"
                  >
                    <Flex direction="column" gap="2" pr="2">
                      {filteredAvailable.length === 0 ? (
                        <Box py="6">
                          <Text as="p" align="center" color="gray" size="2">
                            {available_events.length === 0
                              ? "No more events to add. Create more from the Events tab."
                              : "No events match your search."}
                          </Text>
                        </Box>
                      ) : (
                        filteredAvailable.map((event) => (
                          <EventRow
                            key={event.id}
                            event={event}
                            action="add"
                            onClick={() => addEvent(event.id)}
                          />
                        ))
                      )}
                    </Flex>
                  </ScrollArea>
                </Flex>
              </Card>
            </Flex>
          </Box>
        </Section>
      </Box>
    </div>
  );
}
